import React, { useEffect, useState } from "react";
import { ReactNode } from "react";
import { Push } from "typescript";
import { isExpired, decodeToken } from "react-jwt";
import { APIService } from "../services/API.service";
import { access } from "fs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LoadingOutlined } from "@ant-design/icons";
import { Button, Checkbox, Flex, Form, FormProps, Input, Spin, Typography } from "antd";
import { Decipher } from "crypto";

export enum PushRole {
    None = 0,
    CanUseDeveloperTools = 1,
    CanCreateACampaign = 2,
    CanViewCampaign = 4,
    CanInviteUsers = 8,
    CanCreateOrganisation = 16,
    CanCancelACampaign = 32
}

export interface AuthClaims {
    exp: number, // epoch seconds when the claims expire
    iat: number, // epoch seconds when the claims where issued
    data: {
        isSystemAdmin: boolean,
        apps: string[],
        orgs: {
            apps: {
                id: string,
                roles: PushRole
            }[]
        }[]
    }
}
export interface AuthContext {
    isAuthenticated: boolean
    accessToken: string | null
    setAccessToken: (token:string|null)=>void
    decodedToken: AuthClaims | null
    hasClaims: (appId: string | undefined | null, claims: PushRole) => boolean
    isSystemAdmin: ()=>boolean,
    api: APIService
}

const AuthContext = React.createContext<AuthContext | null>(null)
const accessTokenKey = 'mhq.push.auth.accessToken'

function getStoredAccessToken() {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
        return null;
    }
    let token = localStorage.getItem(accessTokenKey);
    return token;
}

function setStoredAccessToken(accessToken: string | null) {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
        return;
    }
    if (accessToken) {
        localStorage.setItem(accessTokenKey, accessToken)
    } else {
        localStorage.removeItem(accessTokenKey)
    }
}

function decodedAccessToken(accessToken: string | null) {
    let result: AuthClaims | null = null;
    if (accessToken != null) {
        let payload = decodeToken<AuthClaims>(accessToken);
        if (payload != null) {
            result = payload;
        }
    }
    return result;
}

function validDecodedToken(token: AuthClaims | null): boolean {
    let result = false;
    let now = Math.floor(new Date().getTime() / 1000)
    if (token && token.exp > now) {
        result = true;
    }
    return result;
}

export const AuthProvider = (props: { children: ReactNode }) => {
    const [mmContext, setMMContext] = useState<string | null>(null);
    const [isEmbedded, setIsEmbedded] = useState<boolean>(true);
    const [accessToken, setAccessToken] = useState<string | null>(getStoredAccessToken())
    const [decodedToken, setDecodedToken] = useState<AuthClaims | null>(decodedAccessToken(accessToken))
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(validDecodedToken(decodedToken))
    const [api, setApi] = useState<APIService>(new APIService());

    useEffect(() => {
        let token = decodedAccessToken(accessToken);
        if (validDecodedToken(token)) {
            api.setAuthToken(accessToken);
            setIsAuthenticated(validDecodedToken(token));
        }
        setDecodedToken(token);
    }, [accessToken])

    const authMapQuery = useQuery({ enabled: mmContext != null, refetchInterval: 15 * 60 * 1000, queryKey: ['auth', 'mapping'], queryFn: () => api.mapMMContextToPushContext(mmContext) })

    useEffect(() => {
        if (typeof window === 'undefined') return;
        
        let url = new URL(window.location.href);
        let mmContext = url.searchParams.get("mmContext");
        if (!mmContext) {
            setIsEmbedded(false);
            setMMContext(null);
        } else {
            setMMContext(mmContext);
            setIsEmbedded(true);
        }
    }, []);

    useEffect(() => {
        if (authMapQuery.data) {
            setStoredAccessToken(authMapQuery.data);
            setAccessToken(authMapQuery.data);
        } else if (authMapQuery.error) {
            setStoredAccessToken(null);
            setAccessToken(null);
        }
    }, [authMapQuery.data])

    const applyAccessToken = (token:string|null)=>{
        setStoredAccessToken(token);
        setAccessToken(token)
    }

    const isSystemAdmin= ():boolean => {
        return (isAuthenticated && decodedToken && (decodedToken.data.isSystemAdmin || !!(decodedToken.data.orgs.map(o=>o.apps).flat().find(a=>a.roles & PushRole.CanCreateOrganisation)))) ?? false
    }

    const hasClaims = (appId: string | undefined | null, claims: PushRole) => {
        let result = false;
        if (appId != null && appId != undefined && isAuthenticated && decodedToken) {
            let app = decodedToken.data.orgs.map(o => o.apps).flat().find(a => a.id == appId);
            if (app && app.roles & claims || decodedToken.data.isSystemAdmin) {
                result = true;
            }
        }
        return result;
    }
    // 
    return (
        <AuthContext.Provider value={{
            isAuthenticated,
            accessToken,
            setAccessToken:applyAccessToken,
            decodedToken,
            hasClaims,
            api,
            isSystemAdmin

        }} >
            {isAuthenticated ? props.children : (isEmbedded ? <Flex justify="center" vertical style={{height: '100vh'}}><Flex justify="center"><Spin indicator={<LoadingOutlined spin />} size="large" /></Flex></Flex> : <AuthPage />)}
        </AuthContext.Provider>
    )
}

export function useAuth() {
    const context = React.useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context;
}



type FieldType = {
    username?: string;
    password?: string;
    remember?: string;
};


const AuthPage = () => {

    const [inviteCode, setInviteCode] = useState<string|null>(null);

    const [credentials, setCredentials] = useState<{username: string, password: string}|null>(null);
    const auth = useAuth();

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setInviteCode(new URL(window.location.href).searchParams.get("inviteCode"));
        }
    }, []);

    const loginQuery = useQuery({ queryKey: ['auth', 'login'], enabled: credentials != null , queryFn: () => auth.api.login(credentials?.username ?? "", credentials?.password ?? "") });

    useEffect(()=>{
        if(loginQuery.data)
        {
            auth.setAccessToken(loginQuery.data);
        }
    }, [loginQuery.data])

    const onFinish: FormProps<FieldType>['onFinish'] = (values) => {
        // console.log('Success:', values);
        setCredentials({ username: values["username"] ?? "", password: values["password"] ?? ""})
        
    };

    const onFinishFailed: FormProps<FieldType>['onFinishFailed'] = (errorInfo) => {
        // console.log('Failed:', errorInfo);
    };

    return (
        <Flex justify="center">
        <Flex justify="center" vertical style={{height: '100vh'}}>
        <Typography.Title level={2}>Push HQ</Typography.Title>
        <Form
            name="basic"
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            style={{ maxWidth: 600 }}
            initialValues={{ remember: true }}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            autoComplete="off"
        >
            <Form.Item<FieldType>
                label="Username"
                name="username"
                rules={[{ required: true, message: 'Please input your username!' }]}
            >
                <Input />
            </Form.Item>

            <Form.Item<FieldType>
                label="Password"
                name="password"
                rules={[{ required: true, message: 'Please input your password!' }]}
            >
                <Input.Password />
            </Form.Item>

            <Form.Item<FieldType> name="remember" valuePropName="checked" label={null}>
                <Checkbox>Remember me</Checkbox>
            </Form.Item>

            <Form.Item label={null}>
                <Button type="primary" htmlType="submit">
                    Login
                </Button>
            </Form.Item>
        </Form>
        </Flex>
        </Flex>
    )
}